import apiService from './api.service.js';
import logger from '../utils/logger';

/**
 * Periods the dashboard offers, in days. The backend derives the comparison
 * window (same length, immediately before `since`) and picks the series bucket
 * (hour/day/week) from the span — the client does not get to ask for an hourly
 * series over a year.
 */
export const PERIOD_DAYS = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

export const DEFAULT_PERIOD = '30d';

/**
 * Resolve a period key into the ISO instants the endpoint expects.
 *
 * `toISOString()` emits UTC with a trailing `Z`, which is what the Lambda
 * normalizes for. It converts any offset it is given rather than discarding it,
 * so a caller in a non-UTC timezone still gets the window they asked for.
 */
export function periodToRange(period = DEFAULT_PERIOD) {
  const days = PERIOD_DAYS[period] || PERIOD_DAYS[DEFAULT_PERIOD];
  const until = new Date();
  const since = new Date(until.getTime() - days * 24 * 60 * 60 * 1000);
  return { since: since.toISOString(), until: until.toISOString() };
}

class MetricsService {
  constructor() {
    this.baseURL = process.env.REACT_APP_METRICS_API_URL || 'https://zugspus36d.execute-api.us-west-2.amazonaws.com/dev/dashboard';
  }

  /**
   * GET /dashboard/metrics — one account's (or one project's) metrics for a window.
   *
   * Returns the DashboardMetrics shape: { scope, period, coins, workflows,
   * agents, recent_activities }. The old shape (overview / resource_usage /
   * assistant_performance) is gone: three of its metrics read tables that were
   * empty or stale, and the repository swallowed every exception and returned
   * zeros — so a broken query looked exactly like an empty account.
   *
   * @param {Object}  options
   * @param {string}  options.accountId  Target account. Sent as X-Account-Id.
   * @param {?string} options.projectId  Narrow to one project. Falsy = whole account.
   * @param {?string} options.since      ISO-8601. Omit for the backend default (last 30d).
   * @param {?string} options.until      ISO-8601.
   */
  async getDashboardMetrics({ accountId, projectId = null, since = null, until = null } = {}) {
    try {
      // The backend resolves the target account from the X-Account-Id header
      // (Epic 18 authz layer). Callers pass accountId explicitly (from
      // OrganizationContext) to avoid the localStorage race during initial app
      // load; the fallback is for legacy callers.
      const resolvedAccountId = accountId || localStorage.getItem('currentAccountId');
      if (!resolvedAccountId) {
        throw new Error('No active account selected');
      }

      const query = new URLSearchParams();
      if (projectId) query.set('project_id', projectId);
      if (since) query.set('since', since);
      if (until) query.set('until', until);

      const qs = query.toString();
      const endpoint = `${this.baseURL}/metrics${qs ? `?${qs}` : ''}`;

      // apiService.request() returns the parsed JSON body directly — Powertools
      // already builds the API Gateway envelope, so there is nothing to unwrap.
      return await apiService.request(endpoint, {
        method: 'GET',
        headers: {
          'X-Account-Id': resolvedAccountId,
        },
      });
    } catch (error) {
      logger.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  }
}

export default new MetricsService();
